import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Package, Check, X } from "@phosphor-icons/react";
import { fmtDate } from "../lib/date";

// "Assets awaiting acceptance" — recipient inbox. Accept transfers custody once; Decline needs a reason
// and escalates to the admin queue. No auto-transfer ever.
export const AssetAcceptance = () => {
  const [rows, setRows] = useState([]);
  const [declineId, setDeclineId] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/asset-transfers/inbox").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const accept = async (r) => {
    if (busy) return; setBusy(true);
    try { await api.post(`/asset-transfers/${r.id}/accept`); toast.success(`You now hold ${r.asset?.asset_number}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const submitDecline = async () => {
    if (busy) return;
    if (!reason.trim()) { toast.error("A reason is required to decline."); return; }
    setBusy(true);
    try { await api.post(`/asset-transfers/${declineId}/decline`, { reason }); toast.success("Declined — sent to admin."); setDeclineId(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!rows.length) return null;
  return (
    <div className="bg-white border-2 border-amber-300 rounded-md overflow-hidden" data-testid="assets-awaiting-acceptance">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-amber-50">
        <Package size={18} weight="bold" className="text-amber-600" />
        <h3 className="font-display text-base font-bold text-slate-800">Assets awaiting your acceptance</h3>
        <span className="ml-auto font-mono text-xs font-bold px-2 py-0.5 rounded bg-amber-400 text-slate-900">{rows.length}</span>
      </div>
      <div className="p-2 flex flex-col">
        {rows.map((r) => (
          <div key={r.id} data-testid={`awaiting-row-${r.id}`} className="flex flex-col gap-2 px-2 py-2.5 border-b last:border-0 border-slate-100">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-body font-semibold text-slate-900">{r.asset?.asset_number} — {r.asset?.name}</div>
                <div className="text-xs text-slate-500">Offered by {r.from_name} · {fmtDate(r.created_at)}</div>
              </div>
              {declineId !== r.id && (
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`accept-${r.id}`} onClick={() => accept(r)} disabled={busy} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold"><Check size={14} className="mr-1" />Accept</Button>
                  <Button data-testid={`decline-${r.id}`} onClick={() => { setDeclineId(r.id); setReason(""); }} variant="outline" className="h-8 text-xs font-semibold text-red-700 border-red-300"><X size={14} className="mr-1" />Decline</Button>
                </div>
              )}
            </div>
            {declineId === r.id && (
              <div className="flex flex-col gap-2 bg-red-50 border border-red-200 rounded p-2">
                <Textarea data-testid={`decline-reason-${r.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for declining (required)…" className="border-2" />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="h-8 text-xs" onClick={() => { setDeclineId(null); setReason(""); }}>Cancel</Button>
                  <Button data-testid={`decline-submit-${r.id}`} onClick={submitDecline} disabled={busy} className="h-8 bg-red-600 hover:bg-red-700 text-xs font-semibold">Confirm decline</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
